import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AiUsageDimension } from 'src/app/enums/aiUsageDimension.enum';
import { AiUsageByUserGridService } from 'src/app/services/aiUsageByUserGrid.service';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { AiUsageByUserState } from '../ai-usage-by-user.state';
import { formatMonthLabel } from '../ai-usage-by-user.util';

interface GroupingOption {
  value: AiUsageDimension;
  label: string;
}

interface ToolbarView {
  monthLabel: string;
  canStepForward: boolean;
  groupBy: AiUsageDimension;
}

const DEFAULT_LOCALE = 'en';

@Component({
  standalone: false,
  selector: 'app-ai-usage-by-user-toolbar',
  templateUrl: './ai-usage-by-user-toolbar.component.html',
  styleUrls: ['./ai-usage-by-user-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiUsageByUserToolbarComponent {
  private readonly state = inject(AiUsageByUserState);
  private readonly gridService = inject(AiUsageByUserGridService);
  private readonly translateService = inject(TranslateService);

  readonly totals$ = this.gridService.totals$;
  readonly view$: Observable<ToolbarView> = combineLatest([this.state.month$, this.state.groupBy$]).pipe(
    map(([month, groupBy]) => ({
      monthLabel: formatMonthLabel(month, this.locale),
      canStepForward: this.state.canStepForward,
      groupBy
    }))
  );
  readonly groupings: GroupingOption[] = [
    { value: AiUsageDimension.Org, label: 'By org' },
    { value: AiUsageDimension.User, label: 'By user' },
    { value: AiUsageDimension.Month, label: 'By month' }
  ];
  readonly trackByIndex = trackByIndex;

  stepMonth(delta: number): void {
    this.state.stepMonth(delta);
  }

  setGroupBy(groupBy: AiUsageDimension): void {
    this.state.setGroupBy(groupBy);
  }

  private get locale(): string {
    return this.translateService.currentLang || DEFAULT_LOCALE;
  }
}
